import {Audio} from '@remotion/media';
import React from 'react';
import {
	AbsoluteFill,
	Easing,
	Img,
	Sequence,
	interpolate,
	spring,
	staticFile,
	useCurrentFrame,
	useVideoConfig,
} from 'remotion';

const SANS = '-apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", Arial, sans-serif';
const SERIF = '"Songti SC", "STSong", "Noto Serif SC", "SimSun", serif';
const MONO = '"SF Mono", "Menlo", "Courier New", monospace';
const CREAM = '#f3ecdd';
const INK = '#2a231b';
const RED = '#9e2b20';
const GOLD = '#a9824c';
const CLAMP = {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'} as const;

type Station = {
	img: string;
	time: string;
	hour: number;
	cn: string;
	en: string;
	where: string;
};

const STATIONS: Station[] = [
	{img: 'gugong.jpg', time: '05:48', hour: 5.8, cn: '故宫', en: 'THE FORBIDDEN CITY', where: '北京 · 中国'},
	{img: 'bingmayong.jpg', time: '07:12', hour: 7.2, cn: '兵马俑', en: 'TERRACOTTA ARMY', where: '西安 · 中国'},
	{img: 'mogao.jpg', time: '09:30', hour: 9.5, cn: '莫高窟', en: 'MOGAO CAVES', where: '敦煌 · 中国'},
	{img: 'zhuozhengyuan.jpg', time: '11:05', hour: 11.08, cn: '拙政园', en: 'THE HUMBLE ADMINISTRATOR', where: '苏州 · 中国'},
	{img: 'xihu.jpg', time: '13:20', hour: 13.33, cn: '西湖', en: 'WEST LAKE', where: '杭州 · 中国'},
	{img: 'xingping.jpg', time: '15:40', hour: 15.67, cn: '兴坪', en: 'XINGPING', where: '桂林 · 中国'},
	{img: 'fushimi.jpg', time: '17:25', hour: 17.42, cn: '伏见稻荷', en: 'FUSHIMI INARI', where: '京都 · 日本'},
	{img: 'giza.jpg', time: '18:50', hour: 18.83, cn: '吉萨', en: 'GIZA', where: '开罗 · 埃及'},
	{img: 'colosseum.jpg', time: '20:15', hour: 20.25, cn: '斗兽场', en: 'COLOSSEUM', where: '罗马 · 意大利'},
	{img: 'louvre.jpg', time: '21:40', hour: 21.67, cn: '卢浮宫', en: 'THE LOUVRE', where: '巴黎 · 法国'},
	{img: 'centralpark.jpg', time: '23:20', hour: 23.33, cn: '中央公园', en: 'CENTRAL PARK', where: '纽约 · 美国'},
	{img: 'machupicchu.jpg', time: '01:05', hour: 1.08, cn: '马丘比丘', en: 'MACHU PICCHU', where: '库斯科 · 秘鲁'},
];

const tintFor = (hour: number): {background: string; blend: 'multiply' | 'screen' | 'soft-light' | 'normal'} => {
	if (hour < 6.5) return {background: 'rgba(255, 150, 80, 0.22)', blend: 'screen'};
	if (hour < 11) return {background: 'rgba(255, 232, 190, 0.1)', blend: 'screen'};
	if (hour < 15) return {background: 'rgba(255, 255, 255, 0.02)', blend: 'normal'};
	if (hour < 18) return {background: 'rgba(255, 170, 90, 0.16)', blend: 'soft-light'};
	if (hour < 20) return {background: 'rgba(255, 100, 60, 0.24)', blend: 'screen'};
	if (hour < 23) return {background: 'rgba(14, 26, 62, 0.42)', blend: 'multiply'};
	return {background: 'rgba(8, 18, 48, 0.55)', blend: 'multiply'};
};

const Grain: React.FC<{opacity?: number}> = ({opacity = 0.07}) => {
	const frame = useCurrentFrame();
	return (
		<div
			style={{
				position: 'absolute',
				inset: -60,
				backgroundImage: `url(${staticFile('s3/grain.png')})`,
				backgroundRepeat: 'repeat',
				backgroundPosition: `${(frame * 37) % 320}px ${(frame * 59) % 320}px`,
				opacity,
				mixBlendMode: 'overlay',
				pointerEvents: 'none',
			}}
		/>
	);
};

const SunMoon: React.FC<{night: boolean}> = ({night}) => (
	<div style={{position: 'relative', width: 18, height: 18}}>
		<div
			style={{
				position: 'absolute',
				inset: 0,
				borderRadius: '50%',
				backgroundColor: night ? CREAM : '#ffc46b',
				boxShadow: night ? '0 0 12px rgba(243,236,221,0.6)' : '0 0 14px rgba(255,196,107,0.9)',
			}}
		/>
		{night ? (
			<div
				style={{
					position: 'absolute',
					top: -2,
					left: 5,
					width: 15,
					height: 15,
					borderRadius: '50%',
					backgroundColor: 'rgba(20,18,15,0.95)',
				}}
			/>
		) : null}
	</div>
);

const DayBar: React.FC<{hour: number}> = ({hour}) => {
	const left = `${(hour / 24) * 100}%`;
	const night = hour >= 19 || hour < 5.5;
	return (
		<div style={{position: 'absolute', left: 110, right: 110, bottom: 54}}>
			<div style={{position: 'relative', height: 18, marginBottom: 10}}>
				<div style={{position: 'absolute', left: 0, right: 0, top: 8, height: 1, backgroundColor: 'rgba(243,236,221,0.35)'}} />
				{[0, 6, 12, 18, 24].map((mark) => (
					<div
						key={mark}
						style={{
							position: 'absolute',
							left: `${(mark / 24) * 100}%`,
							top: 5,
							width: 1,
							height: 7,
							backgroundColor: 'rgba(243,236,221,0.5)',
						}}
					/>
				))}
				<div style={{position: 'absolute', left, top: 0, transform: 'translateX(-50%)'}}>
					<SunMoon night={night} />
				</div>
			</div>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					fontFamily: MONO,
					fontSize: 13,
					letterSpacing: 1.5,
					color: 'rgba(243,236,221,0.6)',
				}}
			>
				<span>00:00</span>
				<span>06:00</span>
				<span>12:00</span>
				<span>18:00</span>
				<span>24:00</span>
			</div>
		</div>
	);
};

const Stamp: React.FC<{index: number; frame: number}> = ({index, frame}) => {
	const progress = interpolate(frame, [6, 14], [0, 1], {...CLAMP, easing: Easing.out(Easing.cubic)});
	return (
		<div
			style={{
				position: 'absolute',
				right: 96,
				top: 214,
				padding: '12px 20px',
				border: `4px solid ${RED}`,
				outline: '1px solid rgba(158,43,32,0.5)',
				outlineOffset: 4,
				color: RED,
				fontFamily: SERIF,
				textAlign: 'center',
				lineHeight: 1.18,
				letterSpacing: 6,
				opacity: 0.9 * progress,
				transform: `rotate(-9deg) scale(${1 + (1 - progress) * 0.8})`,
				transformOrigin: 'center',
			}}
		>
			<div style={{fontSize: 25, letterSpacing: 4}}>第 {String(index + 1).padStart(2, '0')} 站</div>
			<div style={{fontSize: 34, fontWeight: 700, letterSpacing: 8}}>已盖章</div>
		</div>
	);
};

const Intro: React.FC = () => {
	const frame = useCurrentFrame();
	const {fps} = useVideoConfig();
	const title = spring({frame: frame - 2, fps, config: {damping: 16, stiffness: 110, mass: 0.8}});
	const fade = interpolate(frame, [22, 30], [1, 0], CLAMP);

	return (
		<AbsoluteFill style={{backgroundColor: '#0d0b08', opacity: fade}}>
			<Img
				src={staticFile('yunyou/cover.jpg')}
				style={{
					width: '100%',
					height: '100%',
					objectFit: 'cover',
					filter: 'brightness(0.55) saturate(0.9)',
					transform: `scale(${1.04 + frame * 0.003})`,
				}}
			/>
			<div
				style={{
					position: 'absolute',
					inset: 0,
					background: 'linear-gradient(90deg, rgba(10,8,6,0.92) 0%, rgba(10,8,6,0.55) 52%, rgba(10,8,6,0.2) 100%)',
				}}
			/>
			<div
				style={{
					position: 'absolute',
					left: 132,
					top: 250,
					transform: `translateY(${(1 - title) * 40}px)`,
					opacity: title,
					color: CREAM,
				}}
			>
				<div style={{fontFamily: MONO, fontSize: 15, letterSpacing: 5, color: 'rgba(243,236,221,0.55)'}}>
					A PASSPORT OF TWELVE PLACES
				</div>
				<div style={{display: 'flex', alignItems: 'center', gap: 34, marginTop: 26}}>
					<div style={{fontFamily: SERIF, fontSize: 108, fontWeight: 700, letterSpacing: 12, lineHeight: 1.15}}>
						十二个地方
						<br />
						一天走完
					</div>
					<div
						style={{
							width: 86,
							height: 86,
							border: `4px solid ${RED}`,
							color: RED,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontFamily: MONO,
							fontSize: 28,
							fontWeight: 700,
							letterSpacing: 2,
							transform: 'rotate(-7deg)',
						}}
					>
						24H
					</div>
				</div>
				<div style={{marginTop: 24, fontFamily: SANS, fontSize: 30, letterSpacing: 10, color: 'rgba(243,236,221,0.7)'}}>
					云游十二站 · 世界的一天
				</div>
			</div>
			<Grain opacity={0.08} />
		</AbsoluteFill>
	);
};

const Shot: React.FC<{station: Station; index: number}> = ({station, index}) => {
	const frame = useCurrentFrame();
	const tint = tintFor(station.hour);
	const flash = interpolate(frame, [0, 3], [0.35, 0], CLAMP);
	const zoom = 1.06 + frame * 0.0022;
	const drift = index % 2 === 0 ? frame * 0.8 : -frame * 0.8;

	return (
		<AbsoluteFill style={{backgroundColor: '#0a0908'}}>
			<Img
				src={staticFile(`yunyou/${station.img}`)}
				style={{
					width: '100%',
					height: '100%',
					objectFit: 'cover',
					transform: `scale(${zoom}) translateX(${drift}px)`,
					filter: 'contrast(1.05) saturate(0.95)',
				}}
			/>
			<div
				style={{
					position: 'absolute',
					inset: 0,
					backgroundColor: tint.background,
					mixBlendMode: tint.blend,
				}}
			/>
			<div
				style={{
					position: 'absolute',
					inset: 0,
					background:
						'linear-gradient(180deg, rgba(10,8,6,0.62) 0%, transparent 28%, transparent 70%, rgba(10,8,6,0.7) 100%)',
				}}
			/>

			<div style={{position: 'absolute', left: 116, top: 92, color: CREAM}}>
				<div style={{fontFamily: MONO, fontSize: 64, fontWeight: 500, letterSpacing: 2, lineHeight: 1, textShadow: '0 2px 20px rgba(0,0,0,0.8)'}}>
					{station.time}
				</div>
				<div style={{display: 'flex', alignItems: 'baseline', gap: 20, marginTop: 12}}>
					<div style={{fontFamily: SERIF, fontSize: 58, fontWeight: 700, letterSpacing: 8, textShadow: '0 2px 20px rgba(0,0,0,0.8)'}}>
						{station.cn}
					</div>
					<div style={{fontFamily: SANS, fontSize: 20, letterSpacing: 4, color: 'rgba(243,236,221,0.62)'}}>
						{station.where}
					</div>
				</div>
				<div style={{marginTop: 8, fontFamily: MONO, fontSize: 14, letterSpacing: 3, color: 'rgba(243,236,221,0.42)'}}>
					{station.en}
				</div>
			</div>

			<div
				style={{
					position: 'absolute',
					right: 100,
					top: 100,
					fontFamily: MONO,
					fontSize: 16,
					letterSpacing: 4,
					color: 'rgba(243,236,221,0.55)',
				}}
			>
				STATION {String(index + 1).padStart(2, '0')} / 12
			</div>

			<Stamp index={index} frame={frame} />
			<DayBar hour={station.hour} />

			<div
				style={{
					position: 'absolute',
					inset: 0,
					backgroundColor: CREAM,
					opacity: flash,
					pointerEvents: 'none',
				}}
			/>
			<div
				style={{
					position: 'absolute',
					inset: 0,
					border: `16px solid ${CREAM}`,
					boxShadow: 'inset 0 0 110px rgba(20,14,8,0.4)',
					pointerEvents: 'none',
				}}
			/>
			<Grain opacity={0.08} />
		</AbsoluteFill>
	);
};

const EndCard: React.FC = () => {
	const frame = useCurrentFrame();
	const {fps} = useVideoConfig();
	const title = spring({frame: frame - 2, fps, config: {damping: 18, stiffness: 100}});
	const qr = interpolate(frame, [12, 24], [0, 1], {...CLAMP, easing: Easing.out(Easing.quad)});

	return (
		<AbsoluteFill style={{backgroundColor: CREAM, color: INK}}>
			<div
				style={{
					position: 'absolute',
					inset: 0,
					background:
						'radial-gradient(70% 60% at 85% 10%, rgba(169,130,76,0.16), transparent 60%), radial-gradient(60% 60% at 8% 100%, rgba(158,43,32,0.1), transparent 60%)',
				}}
			/>
			<div
				style={{
					position: 'absolute',
					inset: 0,
					backgroundImage: `url(${staticFile('s3/paper.jpg')})`,
					backgroundSize: 'cover',
					opacity: 0.1,
					mixBlendMode: 'multiply',
				}}
			/>

			<div
				style={{
					position: 'absolute',
					left: 120,
					top: 190,
					width: 600,
					padding: 16,
					backgroundColor: '#fbf8f0',
					transform: `rotate(-2deg) translateY(${(1 - title) * 30}px)`,
					opacity: title,
					boxShadow: '0 40px 90px rgba(42,35,27,0.25)',
				}}
			>
				<Img
					src={staticFile('yunyou/cover.jpg')}
					style={{width: '100%', height: 375, objectFit: 'cover', display: 'block'}}
				/>
				<div
					style={{
						marginTop: 14,
						display: 'flex',
						justifyContent: 'space-between',
						fontFamily: MONO,
						fontSize: 13,
						letterSpacing: 2,
						color: 'rgba(42,35,27,0.55)',
					}}
				>
					<span>PASSPORT · 12 STAMPS</span>
					<span>EST. 2026</span>
				</div>
			</div>

			<div
				style={{
					position: 'absolute',
					left: 150,
					top: 688,
					padding: '10px 18px',
					border: `4px solid ${RED}`,
					outline: '1px solid rgba(158,43,32,0.5)',
					outlineOffset: 4,
					color: RED,
					fontFamily: SERIF,
					letterSpacing: 6,
					transform: `rotate(-8deg) scale(${interpolate(frame, [18, 28], [1.5, 1], {...CLAMP, easing: Easing.out(Easing.cubic)})})`,
					opacity: interpolate(frame, [18, 26], [0, 0.92], CLAMP),
					textAlign: 'center',
					lineHeight: 1.2,
				}}
			>
				<div style={{fontSize: 22, letterSpacing: 4}}>已集齐</div>
				<div style={{fontSize: 30, fontWeight: 700}}>12 / 12</div>
			</div>
			<div style={{position: 'absolute', left: 800, top: 160, width: 1000}}>
				<div style={{fontFamily: MONO, fontSize: 15, letterSpacing: 5, color: RED}}>TRAVEL LOG · 12 STATIONS</div>
				<div
					style={{
						marginTop: 18,
						fontFamily: SERIF,
						fontSize: 110,
						fontWeight: 700,
						letterSpacing: 16,
						opacity: title,
						transform: `translateY(${(1 - title) * 24}px)`,
					}}
				>
					云游十二站
				</div>
				<div style={{marginTop: 8, fontFamily: SANS, fontSize: 34, letterSpacing: 8, color: 'rgba(42,35,27,0.7)'}}>
					十二个地方，一次慢慢走
				</div>

				<div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 34, width: 760}}>
					{Array.from({length: 12}, (_, i) => {
						const p = interpolate(frame, [8 + i * 2, 14 + i * 2], [0, 1], {...CLAMP, easing: Easing.out(Easing.cubic)});
						return (
							<div
								key={i}
								style={{
									width: 54,
									height: 54,
									border: `2px solid ${RED}`,
									color: RED,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontFamily: MONO,
									fontSize: 15,
									letterSpacing: 1,
									opacity: 0.9 * p,
									transform: `rotate(${i % 2 === 0 ? -5 : 4}deg) scale(${0.8 + p * 0.2})`,
								}}
							>
								{String(i + 1).padStart(2, '0')}
							</div>
						);
					})}
				</div>

				<div
					style={{
						marginTop: 38,
						display: 'flex',
						alignItems: 'flex-end',
						gap: 28,
						opacity: qr,
						transform: `translateY(${(1 - qr) * 20}px)`,
					}}
				>
					<div>
						<div style={{fontFamily: SANS, fontSize: 30, fontWeight: 600, letterSpacing: 5}}>扫码领旅行护照</div>
						<div style={{marginTop: 10, fontFamily: MONO, fontSize: 21, letterSpacing: 1, color: 'rgba(42,35,27,0.62)'}}>
							yunyou12.pages.dev
						</div>
						<div style={{marginTop: 10, fontFamily: SANS, fontSize: 16, letterSpacing: 3, color: 'rgba(42,35,27,0.42)'}}>
							十二站 · 十二枚章 · 一张写着你名字的证书
						</div>
					</div>
					<div
						style={{
							width: 204,
							height: 204,
							padding: 14,
							borderRadius: 10,
							backgroundColor: '#fbf8f0',
							boxShadow: '0 18px 50px rgba(42,35,27,0.22)',
						}}
					>
						<Img src={staticFile('yunyou/qr.png')} style={{width: '100%', height: '100%', objectFit: 'contain'}} />
					</div>
				</div>
			</div>

			<div
				style={{
					position: 'absolute',
					left: 120,
					bottom: 44,
					fontFamily: SANS,
					fontSize: 17,
					letterSpacing: 3,
					color: 'rgba(42,35,27,0.45)',
				}}
			>
				一本可以盖章的旅行护照
			</div>
			<div
				style={{
					position: 'absolute',
					right: 100,
					bottom: 44,
					fontFamily: MONO,
					fontSize: 13,
					letterSpacing: 3,
					color: 'rgba(42,35,27,0.35)',
				}}
			>
				ONE DAY · TWELVE PLACES
			</div>
			<Grain opacity={0.05} />
		</AbsoluteFill>
	);
};

export const TravelDay: React.FC = () => {
	return (
		<AbsoluteFill style={{backgroundColor: '#0a0908'}}>
			<Audio src={staticFile('travel.wav')} />
			<Sequence from={0} durationInFrames={30}>
				<Intro />
			</Sequence>
			{STATIONS.map((station, index) => (
				<Sequence key={station.img} from={30 + index * 30} durationInFrames={30}>
					<Shot station={station} index={index} />
				</Sequence>
			))}
			<Sequence from={390} durationInFrames={60}>
				<EndCard />
			</Sequence>
		</AbsoluteFill>
	);
};
